import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuRadioGroup,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Home,
    Key,
    FolderOpen,
    ClipboardList,
    User,
    Building2,
    LogOut,
    CoinsIcon,
    Coins,
    Database,
    Loader2,
    Info,
    Mail,
    Search,
    Settings,
    MapPin,
    MapPinOff,
    Camera,
    Smartphone,
    History,
    Clock,
    ShieldCheck,
    Languages,
} from 'lucide-react';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useBalance } from '@/lib/contexts/balance-context';
import { useAuth } from '@/lib/contexts/auth-context';
import { useTheme } from '@/lib/contexts/theme-context';
import { useLocale } from '@/lib/contexts/locale-context';
import { LocaleMenuItems } from '@/components/ui/app/locale-menu-items';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { useGeolocation } from '@/lib/contexts/device-permissions-context';
import { DialogMobileApp } from '@/pages/_partials/DialogMobileApp';
import SheetPhotoCapture from '@/pages/folders/index/_partials/sheets/SheetPhotoCapture';
import { toast } from 'sonner';

export default function AppLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { balance: tokenBalance, isLoading: isBalanceLoading, refreshBalance } = useBalance();
    const { user, logout } = useAuth();
    const { theme } = useTheme();
    const { locale, setLocale } = useLocale();
    const { can, canOnSelf } = usePermissions();
    const {
        isEnabled: isGeolocationEnabled,
        isLoading: isGeolocationLoading,
        toggleGeolocation,
        permissionStatus: geoPermissionStatus,
        getCurrentPosition,
    } = useGeolocation();

    const hasCompany = Boolean(user?.company_uniq_id ?? user?.company?.uniq_id);
    const isIndividual = user?.role === 'user';
    const isEmailVerified = Boolean(user?.email_verified);
    const [isMobileAppModalOpen, setIsMobileAppModalOpen] = useState(false);
    const [isPhotoCaptureOpen, setIsPhotoCaptureOpen] = useState(false);
    const [isPhotoUploading, setIsPhotoUploading] = useState(false);

    // Blocage pour les contacts d'entreprise sans facture payée
    const billingType = user?.billing_type ?? null; // "contact", "tiers" ou null
    const hasInvoicesPaid = Boolean(user?.has_invoices_paid);
    const isContactWithoutInvoice = billingType === 'contact' && !hasInvoicesPaid;

    // Accès bloqué si email non vérifié OU contact sans facture payée
    const isAccessBlocked = !isEmailVerified || isContactWithoutInvoice;

    const availableTokens = Number.isFinite(Number(tokenBalance)) ? Number(tokenBalance) : null;

    // Routes accessibles même sans email vérifié
    const alwaysAccessiblePaths = ['/dashboard', '/settings/account', '/settings/company'];

    // Handler pour l'upload d'un média (photo ou vidéo) vers la galerie
    const handlePhotoCapture = async ({ mediaType = 'photo', base64, blob, fileName, mimeType, durationMs }) => {
        // Vérifier que la géolocalisation est activée
        if (!isGeolocationEnabled) {
            toast.error(t('common:geolocation.enableToCapture'));
            return;
        }

        setIsPhotoUploading(true);

        try {
            // Obtenir la géolocalisation
            const position = await getCurrentPosition();

            if (!position) {
                toast.error(t('common:geolocation.positionFailed'));
                return;
            }

            const { latitude, longitude } = position;
            const isVideo = mediaType === 'video';

            await horodatyApi.createPicture({
                file_name: fileName,
                latitude,
                longitude,
                ...(isVideo ? { video: blob, mime_type: mimeType, duration_ms: durationMs } : { base64 }),
            });
            toast.success(isVideo ? t('common:geolocation.videoAdded') : t('common:geolocation.photoAdded'));

            setIsPhotoCaptureOpen(false);
            refreshBalance();
        } catch (error) {
            console.error('Erreur upload média:', error);
            const message = error?.response?.data?.message || t('common:geolocation.mediaUploadFailed');
            toast.error(message);
        } finally {
            setIsPhotoUploading(false);
        }
    };

    // Fonction pour formater les nombres avec suffixes K, M, etc.
    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        if (num < 1000) return num.toString();
        if (num < 1000000) return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1) + 'K';
        return (num / 1000000).toFixed(num % 1000000 === 0 ? 0 : 1) + 'M';
    };

    const menuItems = [
        { path: '/dashboard', label: t('common:nav.home'), icon: Home },
        ...(isIndividual || canOnSelf('VIEW_TOKENS')
            ? [
                  {
                      path: '/tokens',
                      label: t('common:nav.tokens'),
                      icon: Coins,
                      badge: availableTokens,
                      showBadge: true,
                      requiresVerification: true,
                  },
              ]
            : []),
        { path: '/folders', label: t('common:nav.folders'), icon: FolderOpen, requiresVerification: true },
        ...(!isIndividual && (canOnSelf('UPDATE_PROCEDURES') || canOnSelf('UPDATE_PROCEDURE_REPORTS'))
            ? [{ path: '/procedures', label: t('common:nav.procedures'), icon: ClipboardList, requiresVerification: true }]
            : []),
    ];

    return (
        <>
            {/* Header - Desktop only */}
            <header className="desktop-only hidden md:block bg-background border-b border-border shrink-0">
                <div className="relative flex flex-wrap items-center px-4 md:px-6 py-3 gap-x-2 gap-y-3">
                    {/* Left section: Logo */}
                    <Link
                        to="/dashboard"
                        className="theme-logo-container flex-1 h-8 min-w-32 items-center gap-3 shrink-0"
                    >
                        <img
                            src={theme.logo}
                            alt={theme.logoAlt}
                            className="theme-logo theme-logo-desktop h-full w-full object-contain object-left"
                        />
                    </Link>

                    {/* Center section: Navigation - takes available space and centers content */}
                    <nav className="hidden md:flex flex-2 items-center justify-end xl:justify-center gap-1 xl:absolute xl:left-1/2 xl:-translate-x-1/2">
                        {menuItems.map((item) => {
                            const isActive = location.pathname.startsWith(item.path);
                            const isDisabled = item.requiresVerification && isAccessBlocked;

                            if (isDisabled) {
                                const tooltipMessage = isContactWithoutInvoice
                                    ? t('common:nav.featureUnavailable')
                                    : t('common:nav.verifyEmailToAccess');
                                return (
                                    <div
                                        key={item.path}
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg text-muted-foreground cursor-not-allowed"
                                        title={tooltipMessage}
                                    >
                                        <span className="text-sm">{item.label}</span>
                                        {item.showBadge && (
                                            <span className="px-2 py-0.5 bg-surface-active text-muted-foreground text-xs font-semibold rounded-full min-w-[24px] flex items-center justify-center">
                                                {isBalanceLoading ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    (item.badge ?? 0)
                                                )}
                                            </span>
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                                        isActive
                                            ? 'bg-dark/10 text-foreground font-medium'
                                            : 'text-muted-foreground hover:bg-surface-hover dark:hover:bg-surface-hover'
                                    }`}
                                >
                                    <span className="text-sm">{item.label}</span>
                                    {item.showBadge && (
                                        <span className="px-2 py-0.5 bg-secondary text-secondary-foreground text-xs font-semibold rounded-full min-w-[24px] flex items-center justify-center">
                                            {isBalanceLoading ? (
                                                <Loader2 className="w-3 h-3 animate-spin" />
                                            ) : (
                                                (item.badge ?? 0)
                                            )}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* Right section: Search Bar & Actions */}
                    <div className="flex flex-1 items-center justify-end gap-2 md:gap-3 shrink-0 ml-auto">
                        {/* Search Bar - Cliquable */}
                        <Button
                            variant="outline"
                            color="neutral"
                            onClick={() => navigate('/search')}
                            className="justify-start bg-surface hover:bg-surface-hover xl:min-w-[280px] max-xl:border-none! max-xl:bg-muted max-xl:hover:bg-muted/80"
                        >
                            <Search className="w-5 h-5 text-muted-foreground" />
                            <span className="hidden xl:inline text-sm text-muted-foreground">
                                {t('common:search.placeholder')}
                            </span>
                        </Button>

                        {/* Géolocalisation */}
                        <Button
                            variant="soft"
                            color="neutral"
                            size="xmd"
                            onClick={toggleGeolocation}
                            disabled={isGeolocationLoading}
                            title={
                                isGeolocationEnabled && geoPermissionStatus === 'granted'
                                    ? t('common:geolocation.enabledTooltip')
                                    : geoPermissionStatus === 'denied'
                                      ? t('common:geolocation.deniedTooltip')
                                      : t('common:geolocation.enableTooltip')
                            }
                        >
                            {isGeolocationLoading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : isGeolocationEnabled && geoPermissionStatus === 'granted' ? (
                                <MapPin className="w-5 h-5" />
                            ) : (
                                <MapPinOff className="w-5 h-5" />
                            )}
                        </Button>

                        {/* Settings Dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="soft" color="neutral" size="xmd">
                                    <Settings className="w-5 h-5" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem
                                    onClick={() => navigate('/settings/account')}
                                    className="cursor-pointer"
                                >
                                    <User className="w-4 h-4 mr-2" />
                                    {t('common:nav.myAccount')}
                                </DropdownMenuItem>
                                {hasCompany && !isIndividual && (
                                    <DropdownMenuItem
                                        onClick={() => navigate('/settings/company')}
                                        className="cursor-pointer"
                                    >
                                        <Building2 className="w-4 h-4 mr-2" />
                                        {t('common:nav.myCompany')}
                                    </DropdownMenuItem>
                                )}
                                {user?.role === 'admin' && (
                                    <DropdownMenuItem
                                        onClick={() => navigate('/settings/superadmin')}
                                        className="cursor-pointer"
                                    >
                                        <ShieldCheck className="w-4 h-4 mr-2" />
                                        {t('common:nav.superAdmin')}
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                    onClick={() => navigate('/settings/devices')}
                                    className="cursor-pointer"
                                >
                                    <Smartphone className="w-4 h-4 mr-2" />
                                    {t('common:nav.deviceManagement')}
                                </DropdownMenuItem>

                                {!isIndividual && (
                                    <DropdownMenuItem
                                        onClick={() => navigate('/settings/company#actions-history')}
                                        className="cursor-pointer"
                                    >
                                        <Clock className="w-4 h-4 mr-2" />
                                        {t('common:nav.actionHistory')}
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuSub>
                                    <DropdownMenuSubTrigger className="cursor-pointer">
                                        <Languages className="w-4 h-4 mr-2" />
                                        {t('common:language.switch')}
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent>
                                        <DropdownMenuRadioGroup value={locale} onValueChange={setLocale}>
                                            <LocaleMenuItems />
                                        </DropdownMenuRadioGroup>
                                    </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={logout}
                                    className="cursor-pointer text-destructive focus:text-destructive"
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    {t('common:nav.logout')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </header>

            {/* Main Content - flex-1 avec scroll interne, min-h-0 pour éviter débordement */}
            <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
                {/* Bandeau de blocage - affiché seulement si user chargé et accès bloqué */}
                {user && isAccessBlocked && (
                    <div className="bg-warning text-primary-foreground px-4 py-3 flex items-center justify-center gap-2 shrink-0">
                        <Mail className="w-5 h-5 shrink-0" />
                        <p className="text-sm">
                            {!isEmailVerified ? (
                                <>
                                    {t('common:accessBlocked.emailNotVerified')}{' '}
                                    <Link to="/settings/account" className="underline font-medium hover:opacity-80">
                                        {t('common:accessBlocked.goToProfile')}
                                    </Link>{' '}
                                    {t('common:accessBlocked.resendLinkSuffix')}
                                </>
                            ) : (
                                <>{t('common:accessBlocked.firstOrderRequired')}</>
                            )}
                        </p>
                    </div>
                )}

                {/* Zone scrollable - prend tout l'espace restant */}
                <div className="flex-1 overflow-y-auto overscroll-contain">
                    <Outlet />
                </div>
            </main>

            {/* Mobile Bottom Navigation - fixed avec safe-area */}
            {/* Visible sur: Dashboard, Folders, Procedures, Tokens, Account */}
            {(location.pathname === '/dashboard' ||
                location.pathname.startsWith('/folders') ||
                location.pathname === '/procedures' ||
                location.pathname === '/search' ||
                location.pathname.startsWith('/account') ||
                location.pathname.startsWith('/settings')) && (
                <nav className="mobile-only md:hidden fixed bottom-0 left-0 right-0 bg-background border-t border-border z-40 pb-safe">
                    <div className="flex items-center justify-around h-16 relative">
                        {/* Home */}
                        <Link
                            to="/dashboard"
                            className={`flex flex-col items-center justify-center flex-1 h-full ${location.pathname === '/dashboard' ? 'text-icon-active' : 'text-icon-default'}`}
                        >
                            <Home className="w-6 h-6" />
                        </Link>

                        {/* Search */}
                        <Link
                            to="/search"
                            className={`flex flex-col items-center justify-center flex-1 h-full ${location.pathname === '/search' ? 'text-icon-active' : 'text-icon-default'}`}
                        >
                            <Search className="w-6 h-6" />
                        </Link>

                        {/* Camera - Central FAB */}
                        {canOnSelf('CAPTURE_CERTIFIED_PHOTOS') && (
                            <div className="flex-1 flex justify-center">
                                <Button
                                    variant="solid"
                                    color="secondary"
                                    className="absolute -top-2 p-0 w-14 h-14 bg-secondary rounded-full flex items-center justify-center shadow-lg hover:bg-secondary-hover transition-colors"
                                    onClick={() => setIsPhotoCaptureOpen(true)}
                                >
                                    <Camera className="w-7 h-7 text-secondary-foreground" />
                                    {(isIndividual || canOnSelf('VIEW_COMPANY_TOKENS')) &&
                                        tokenBalance !== null &&
                                        tokenBalance > 0 && (
                                            <span className="absolute -top-1 right-1 translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold rounded-full min-w-6 max-w-[42px] h-6 px-1.5 flex items-center justify-center overflow-hidden">
                                                {formatNumber(tokenBalance)}
                                            </span>
                                        )}
                                </Button>
                            </div>
                        )}

                        {/* Folders */}
                        <Link
                            to="/folders"
                            className={`flex flex-col items-center justify-center flex-1 h-full ${location.pathname.startsWith('/folders') ? 'text-icon-active' : 'text-icon-default'}`}
                        >
                            <FolderOpen className="w-6 h-6" />
                        </Link>

                        {/* Procedures */}
                        {!isIndividual && (canOnSelf('UPDATE_PROCEDURES') || canOnSelf('UPDATE_PROCEDURE_REPORTS')) && (
                            <Link
                                to="/procedures"
                                className={`flex flex-col items-center justify-center flex-1 h-full ${location.pathname.startsWith('/procedures') ? 'text-icon-active' : 'text-icon-default'}`}
                            >
                                <ClipboardList className="w-6 h-6" />
                            </Link>
                        )}
                    </div>
                </nav>
            )}

            <DialogMobileApp open={isMobileAppModalOpen} onOpenChange={setIsMobileAppModalOpen} />

            <SheetPhotoCapture
                open={isPhotoCaptureOpen}
                onClose={() => setIsPhotoCaptureOpen(false)}
                onCapture={handlePhotoCapture}
                isUploading={isPhotoUploading}
            />
        </>
    );
}
